import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Sliders, GripVertical } from "lucide-react";

const ENTITIES = [
  { key: "inward", label: "Inward Stock" },
  { key: "outward", label: "Outward Stock" },
  { key: "vendor", label: "Vendors" },
  { key: "customer", label: "Customers" },
];

const TYPES = ["text", "number", "date", "select"];

export default function CustomFieldsManager() {
  const [entity, setEntity] = useState("inward");
  const [fields, setFields] = useState([]);
  const [newField, setNewField] = useState(initialField());
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  function initialField() {
    return { label: "", field_type: "text", options_str: "", required: false };
  }

  const load = async () => {
    const r = await api.get(`/custom-fields?entity=${entity}`);
    setFields(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entity]);

  const add = async (e) => {
    e.preventDefault();
    if (!newField.label.trim()) return;
    const options = newField.field_type === "select"
      ? newField.options_str.split(",").map((s) => s.trim()).filter(Boolean)
      : [];
    if (newField.field_type === "select" && options.length === 0) {
      toast.error("Select fields need at least one comma-separated option");
      return;
    }
    try {
      await api.post("/custom-fields", {
        entity,
        label: newField.label,
        field_type: newField.field_type,
        options,
        required: newField.required,
      });
      toast.success("Custom field added");
      setNewField(initialField());
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this custom field? Existing records keep their stored values.")) return;
    try { await api.delete(`/custom-fields/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const persistOrder = async (nextFields) => {
    try {
      await api.patch("/custom-fields/reorder", {
        entity,
        ordered_ids: nextFields.map((f) => f.id),
      });
    } catch (e) {
      toast.error("Reorder failed");
      load();
    }
  };

  const onDragStart = (idx) => (e) => {
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = "move";
    // Firefox needs data set to initiate drag
    e.dataTransfer.setData("text/plain", String(idx));
  };
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overIndex !== idx) setOverIndex(idx);
  };
  const onDrop = (idx) => (e) => {
    e.preventDefault();
    const from = dragIndex;
    setDragIndex(null);
    setOverIndex(null);
    if (from === null || from === idx) return;
    const next = [...fields];
    const [moved] = next.splice(from, 1);
    next.splice(idx, 0, moved);
    setFields(next);
    persistOrder(next);
  };
  const onDragEnd = () => { setDragIndex(null); setOverIndex(null); };

  const moveBy = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= fields.length) return;
    const next = [...fields];
    [next[idx], next[target]] = [next[target], next[idx]];
    setFields(next);
    persistOrder(next);
  };

  return (
    <section data-testid="custom-fields-section">
      <div className="flex items-center gap-2 mb-3">
        <Sliders className="w-4 h-4 text-[#16A34A]" />
        <div className="font-display text-lg uppercase text-zinc-900">Custom Fields</div>
      </div>

      <div className="border border-zinc-200 bg-white p-4 space-y-4">
        <div className="flex flex-wrap gap-2" data-testid="cf-entity-tabs">
          {ENTITIES.map((e) => (
            <button
              key={e.key}
              onClick={() => setEntity(e.key)}
              data-testid={`cf-entity-${e.key}`}
              className={`px-4 py-2 border text-xs uppercase font-bold tracking-widest transition-colors ${
                entity === e.key ? "bg-[#16A34A] border-[#16A34A] text-white" : "bg-white border-zinc-200 text-zinc-600 hover:border-[#16A34A] hover:text-[#16A34A]"
              }`}
            >
              {e.label}
            </button>
          ))}
        </div>

        <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-5 gap-3 border-t border-zinc-200 pt-4" data-testid="cf-add-form">
          <div className="md:col-span-2">
            <div className="overline mb-2">Field Label</div>
            <input required value={newField.label} onChange={(e) => setNewField({ ...newField, label: e.target.value })} placeholder="e.g. Discount %, HSN Code…" className="input-field" data-testid="cf-label" />
          </div>
          <div>
            <div className="overline mb-2">Type</div>
            <select value={newField.field_type} onChange={(e) => setNewField({ ...newField, field_type: e.target.value })} className="input-field" data-testid="cf-type">
              {TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="md:col-span-1">
            <div className="overline mb-2">{newField.field_type === "select" ? "Options (comma sep.)" : "Options (n/a)"}</div>
            <input value={newField.options_str} onChange={(e) => setNewField({ ...newField, options_str: e.target.value })} disabled={newField.field_type !== "select"} placeholder="A, B, C" className="input-field disabled:bg-zinc-50 disabled:text-zinc-400" data-testid="cf-options" />
          </div>
          <div className="flex items-end gap-3">
            <label className="flex items-center gap-2 text-xs font-semibold text-zinc-700 uppercase tracking-widest cursor-pointer">
              <input type="checkbox" checked={newField.required} onChange={(e) => setNewField({ ...newField, required: e.target.checked })} className="w-4 h-4 accent-[#16A34A]" data-testid="cf-required" />
              Required
            </label>
            <button type="submit" className="btn-primary flex items-center gap-2" data-testid="cf-submit">
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
        </form>

        <div className="border-t border-zinc-200 pt-4">
          {fields.length === 0 ? (
            <div className="text-center py-6 text-zinc-500 font-sans text-sm">No custom fields defined for {ENTITIES.find((e) => e.key === entity)?.label}.</div>
          ) : (
            <>
              <div className="overline mb-3 flex items-center gap-2">
                <GripVertical className="w-3 h-3" />
                Drag to reorder · Applies to forms immediately
              </div>
              <ul className="border border-zinc-200 divide-y divide-zinc-200" data-testid="cf-list">
                {fields.map((f, idx) => (
                  <li
                    key={f.id}
                    draggable
                    onDragStart={onDragStart(idx)}
                    onDragOver={onDragOver(idx)}
                    onDrop={onDrop(idx)}
                    onDragEnd={onDragEnd}
                    data-testid={`cf-item-${f.id}`}
                    className={`px-3 py-3 flex items-center gap-3 bg-white transition-colors ${
                      dragIndex === idx ? "opacity-40" : ""
                    } ${overIndex === idx && dragIndex !== idx ? "bg-[#16A34A]/10 border-l-2 border-[#16A34A]" : ""}`}
                  >
                    <button
                      type="button"
                      className="cursor-grab active:cursor-grabbing text-zinc-400 hover:text-[#16A34A] transition-colors"
                      title="Drag to reorder"
                      data-testid={`cf-drag-${f.id}`}
                      aria-label={`Reorder ${f.label}`}
                    >
                      <GripVertical className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-1 text-zinc-400 font-mono text-xs w-8">
                      <span data-testid={`cf-order-${f.id}`}>{idx + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-zinc-900 text-sm">{f.label}{f.required && <span className="text-[#DC2626] ml-1">*</span>}</div>
                      <div className="text-xs text-zinc-500 font-mono uppercase tracking-widest mt-1">
                        key: {f.field_key} · type: {f.field_type}
                        {f.field_type === "select" && f.options?.length > 0 && ` · options: ${f.options.join(", ")}`}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => moveBy(idx, -1)} disabled={idx === 0} className="px-1.5 py-0.5 text-xs font-bold text-zinc-500 hover:text-[#16A34A] disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Move up" data-testid={`cf-up-${f.id}`}>▲</button>
                      <button type="button" onClick={() => moveBy(idx, 1)} disabled={idx === fields.length - 1} className="px-1.5 py-0.5 text-xs font-bold text-zinc-500 hover:text-[#16A34A] disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Move down" data-testid={`cf-down-${f.id}`}>▼</button>
                    </div>
                    <button onClick={() => remove(f.id)} className="text-zinc-400 hover:text-[#DC2626] transition-colors" data-testid={`cf-del-${f.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
